import DevicesIcon from '@mui/icons-material/Devices'
import LockResetIcon from '@mui/icons-material/LockReset'
import { Alert, Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material'
import { useState, type ReactNode } from 'react'

import { api, ApiError } from '../api/client'
import { useAuth } from '../auth/useAuth'
import ChangePasswordForm from '../components/ChangePasswordForm'
import { exactTime, relativeTime } from '../lib/time'

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 160 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {value}
      </Typography>
    </Stack>
  )
}

export default function MyAccountPage() {
  const { user, refresh } = useAuth()
  const [revoked, setRevoked] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (!user) return null

  async function signOutElsewhere() {
    setBusy(true)
    setError(null)
    try {
      const result = await api.revokeOtherSessions()
      setRevoked(result.revoked)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not end the other sessions.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h2">
        My Account
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Stack
          direction="row"
          spacing={4}
          useFlexGap
          flexWrap="wrap"
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Fact label="Username" value={user.username} />
          <Fact
            label="Role"
            value={
              <Chip
                size="small"
                label={user.role}
                color={user.role === 'sysadmin' ? 'primary' : 'default'}
                variant={user.role === 'sysadmin' ? 'filled' : 'outlined'}
              />
            }
          />
          <Fact label="Account created" value={exactTime(user.created_at)} />
          <Fact
            label="Last signed in"
            value={user.last_login_at ? relativeTime(user.last_login_at) : 'This is your first'}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          A username cannot be changed — the audit trail refers to it, and renaming would detach
          your history from you. A sysadmin can change your role.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, maxWidth: 480 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <LockResetIcon color="primary" />
            <Typography variant="h6" component="h3">
              Change password
            </Typography>
          </Stack>
          <Alert severity="info">
            Changing it signs you out everywhere, including here, so you will be asked to sign in
            again with the new password.
          </Alert>
          <ChangePasswordForm
            submitLabel="Change Password"
            busyLabel="Saving…"
            // The server revokes every session with the old password, this one
            // included, so re-reading the session is what sends us to sign in
            // rather than leaving a dead page on screen.
            onDone={refresh}
          />
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, maxWidth: 480 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <DevicesIcon color="primary" />
            <Typography variant="h6" component="h3">
              Other sessions
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Signs you out of every other browser and device. Use it if you signed in somewhere you
            no longer have. This session stays signed in.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}
          {revoked !== null && !error && (
            <Alert severity="success">
              {revoked === 0
                ? 'You were not signed in anywhere else.'
                : `Ended ${revoked} other session${revoked === 1 ? '' : 's'}.`}
            </Alert>
          )}

          <Box>
            <Button variant="outlined" disabled={busy} onClick={() => void signOutElsewhere()}>
              {busy ? 'Ending…' : 'Sign out everywhere else'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Stack>
  )
}
